import { createRouter, createWebHistory } from 'vue-router'
import { scrollToAnchor } from '@/utils/scrollToAnchor'
import {
  rolePages,
  marketPages,
  nichePages,
  comparisonPages,
  blogPosts,
} from '@/config/seoPages'

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import(/* webpackChunkName: "home" */ '../views/HomeView.vue'),
    meta: {
      title: 'VA House | Multi-Skilled Virtual Assistants for Admin, Support & Calls',
      exactTitle: true,
      description:
        'VA House gives you one agent with a main skill and three secondary skills across admin, customer support, and call handling — not three separate hires. Managed, vetted, and backed by a team for reliability.',
    },
  },
  {
    path: '/pricing',
    name: 'pricing',
    component: () => import(/* webpackChunkName: "pricing" */ '../views/PricingView.vue'),
    meta: {
      title: 'Pricing',
      description: 'Transparent pricing for managed Filipino virtual assistant plans — Starter through Enterprise.',
    },
  },
  {
    path: '/about',
    name: 'about',
    component: () => import(/* webpackChunkName: "about" */ '../views/AboutView.vue'),
    meta: {
      title: 'About',
      description: 'Learn about VA House, our managed service model, and how we build reliable VA operations.',
    },
  },
  {
    path: '/services',
    name: 'services',
    component: () => import(/* webpackChunkName: "services" */ '../views/ServicesView.vue'),
    meta: {
      title: 'VA Services | Multi-Skilled Agents for Admin, Support & Calls',
      exactTitle: true,
      description:
        'VA House agents are matched by a main skill — admin, customer support, or calls — with working proficiency in the other two. Specialist seats, pods, and full teams available.',
    },
  },
  {
    path: '/case-studies',
    name: 'case-studies',
    component: () => import(/* webpackChunkName: "case-studies" */ '../views/CaseStudiesView.vue'),
    meta: {
      title: 'Case Studies',
      description: 'See real examples of how managed virtual assistants improve team capacity and operations.',
    },
  },
  {
    path: '/testimonials',
    name: 'testimonials',
    component: () => import(/* webpackChunkName: "testimonials" */ '../views/TestimonialsView.vue'),
    meta: {
      title: 'Client Testimonials & Reviews',
      description:
        'Read what founders and operators say about VA House — managed Filipino virtual assistants for US, UK, Australia, Canada, and more.',
    },
  },
  {
    path: '/faq',
    name: 'faq',
    component: () => import(/* webpackChunkName: "faq" */ '../views/FaqView.vue'),
    meta: {
      title: 'FAQ',
      description: 'Common questions about onboarding, management, pricing, and scaling with VA House.',
    },
  },
  {
    path: '/how-it-works',
    name: 'how-it-works',
    component: () => import(/* webpackChunkName: "how-it-works" */ '../views/HowItWorksView.vue'),
    meta: {
      title: 'How It Works',
      description: 'Learn how VA House matches, onboards, and manages Filipino virtual assistants for your business.',
    },
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import(/* webpackChunkName: "contact" */ '../views/ContactView.vue'),
    meta: {
      title: 'Contact',
      description: 'Get in touch with VA House to find the right managed virtual assistant plan for your business.',
    },
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () => import(/* webpackChunkName: "privacy" */ '../views/PrivacyPolicyView.vue'),
    meta: {
      title: 'Privacy Policy',
      description: 'How VA House handles and protects information shared through this website.',
    },
  },
  {
    path: '/terms-of-service',
    name: 'terms-of-service',
    component: () => import(/* webpackChunkName: "terms" */ '../views/TermsOfServiceView.vue'),
    meta: {
      title: 'Terms of Service',
      description: 'Terms governing use of the VA House website and service inquiry channels.',
    },
  },
  {
    path: '/blog',
    name: 'blog',
    component: () => import(/* webpackChunkName: "blog" */ '../views/blog/BlogIndexView.vue'),
    meta: {
      title: 'Blog',
      description: 'Guides on hiring virtual assistants, Filipino VA costs, and building remote teams.',
    },
  },
  {
    path: '/blog/:slug',
    name: 'blog-post',
    component: () => import(/* webpackChunkName: "blog-post" */ '../views/blog/BlogPostView.vue'),
  },
  ...rolePages.map((page) => ({
    path: page.path,
    name: `role-${page.slug}`,
    component: () => import(/* webpackChunkName: "role-landing" */ '../views/seo/RoleLandingView.vue'),
    meta: {
      roleSlug: page.slug,
      title: page.title,
      description: page.metaDescription,
    },
  })),
  {
    path: '/va/:role',
    name: 'role-landing',
    component: () => import(/* webpackChunkName: "role-landing" */ '../views/seo/RoleLandingView.vue'),
  },
  ...marketPages.map((page) => ({
    path: page.path,
    name: `market-${page.slug}`,
    component: () => import(/* webpackChunkName: "market-landing" */ '../views/seo/MarketLandingView.vue'),
    meta: {
      marketSlug: page.slug,
      title: page.title,
      description: page.metaDescription,
    },
  })),
  ...nichePages.map((page) => ({
    path: page.path,
    name: `niche-${page.slug}`,
    component: () => import(/* webpackChunkName: "niche-landing" */ '../views/seo/NicheLandingView.vue'),
    meta: {
      nicheSlug: page.slug,
      title: page.title,
      description: page.metaDescription,
    },
  })),
  ...comparisonPages.map((page) => ({
    path: page.path,
    name: `comparison-${page.slug}`,
    component: () => import(/* webpackChunkName: "comparison" */ '../views/seo/ComparisonView.vue'),
    meta: {
      comparisonSlug: page.slug,
      title: page.title,
      description: page.metaDescription,
    },
  })),
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import(/* webpackChunkName: "not-found" */ '../views/NotFoundView.vue'),
    meta: {
      title: 'Page Not Found',
      description: 'The page you requested could not be found.',
    },
  },
]

function validateSeoRegistry(pages, label) {
  const paths = []
  pages.forEach((page) => {
    if (!page.slug || !page.path) {
      throw new Error(`Invalid ${label} config: ${page.title || page.slug || 'unknown'}`)
    }
    if (paths.includes(page.path)) {
      throw new Error(`Duplicate ${label} path: ${page.path}`)
    }
    paths.push(page.path)
  })
}

validateSeoRegistry(rolePages, 'role page')
validateSeoRegistry(marketPages, 'market page')
validateSeoRegistry(nichePages, 'niche page')
validateSeoRegistry(comparisonPages, 'comparison page')

blogPosts.forEach((post) => {
  if (!post.slug || !post.title) {
    throw new Error(`Invalid blog post config: ${post.title || post.slug || 'unknown'}`)
  }
})

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return scrollToAnchor(to.hash, { behavior: 'smooth' }).then((scrolled) =>
        scrolled ? false : { top: 0, behavior: 'smooth' },
      )
    }
    return { top: 0, behavior: 'smooth' }
  },
})

export default router
